import React, { useEffect, useState } from 'react'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import Panel from '../../components/Panel'
import Button from '../../components/Form/Button'
import Badge from '../../components/Badge'
import Input from '../../components/Form/Input'
import ComboboxAutocomplete from '../../components/Form/ComboboxAutocomplete'
import { CustomContentModal } from '../../components/CustomContentModal'
import Modal from '../../components/Modal'
import ModifiedDescriptionField from '../programs/programOnboarding/ModifiedDescriptionField'
import { useAppDispatch } from '../../shared/redux/hooks'
import { pushToast } from '../../components/Toaster/Toaster.slice'
import { formatUTCDate } from '../../util/string'
import { getApiErrorMessage } from '../../apis/apiError'
import {
  createEnergyTaxApi,
  getEnergyTaxCatalogApi,
  getPropertyUtilityOptionsApi,
  linkPropertyUtilityApi,
  listPropertyUtilitiesApi,
  listPropertyUtilityTaxesApi,
  syncPropertyUtilityTaxesApi,
  unlinkPropertyUtilityApi,
} from '../../apis/apis'
import type {
  EnergyTaxCatalogItem,
  PropertyUtilityRow,
  PropertyUtilityTaxRow,
  SyncPropertyUtilityTaxesResult,
  UtilityChargeType,
  UtilityOption,
} from '../../apis/types'
import { UTILITY_CHARGE_TYPES } from '../../apis/types'

interface PropertyUtilitiesAndTaxesPanelProps {
  propertyId: string
  propertyName?: string
  canModify: boolean
}

const utcTodayDateOnly = (): string => new Date().toISOString().slice(0, 10)

const toDateOnlyPart = (value: string): string => value.slice(0, 10)

/** Coerces legacy scalar payloads and orders charge types like the backend enum. */
const normalizeApplicableChargeTypes = (value: unknown): string[] => {
  const raw = Array.isArray(value)
    ? value.map(String)
    : value != null && value !== ''
      ? [String(value)]
      : []
  const selected = new Set(raw)
  return UTILITY_CHARGE_TYPES.filter((c) => selected.has(c))
}

const formatChargeTypes = (chargeTypes: unknown, emptyLabel: string): string => {
  const normalized = normalizeApplicableChargeTypes(chargeTypes)
  return normalized.length > 0 ? normalized.join(', ') : emptyLabel
}

/** Inclusive UTC calendar-day window; matches backend energy-tax effectiveOn semantics. */
const isCurrentlyEffective = (tax: EnergyTaxCatalogItem): boolean => {
  const today = utcTodayDateOnly()
  if (tax.effectiveDate != null && toDateOnlyPart(tax.effectiveDate) > today) return false
  if (tax.endDate != null && toDateOnlyPart(tax.endDate) < today) return false
  return true
}

// --------------------------------------------------------------------------
// Link utility modal
// --------------------------------------------------------------------------

interface LinkUtilityModalProps {
  isOpen: boolean
  propertyId: string
  availableUtilities: UtilityOption[]
  onClose: () => void
  onLinked: () => void
}

const LinkUtilityModal: React.FC<LinkUtilityModalProps> = ({ isOpen, propertyId, availableUtilities, onClose, onLinked }) => {
  const dispatch = useAppDispatch()
  const [utilityCuid, setUtilityCuid] = useState<string | null>(null)
  const [modifiedDescription, setModifiedDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setUtilityCuid(null)
      setModifiedDescription('')
      setError(null)
    }
  }, [isOpen])

  const handleSubmit = async (): Promise<void> => {
    if (utilityCuid == null) {
      setError('Select a utility to link.')
      return
    }
    if (modifiedDescription.trim() === '') {
      setError('Change reason is required.')
      return
    }
    setIsSaving(true)
    setError(null)
    try {
      await linkPropertyUtilityApi(propertyId, { utilityCuid, modifiedDescription: modifiedDescription.trim() })
      await dispatch(pushToast({ type: 'success', message: 'Utility linked' }))
      onLinked()
      onClose()
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Something went wrong. Please try again.'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <CustomContentModal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white dark:bg-slate-800 px-5 py-4 w-full max-w-lg mx-auto shadow-xl rounded-2xl flex flex-col">
        <h2 className="mb-4 text-xl font-medium dark:text-white">Link utility</h2>
        <div className="flex flex-col gap-4">
          <ComboboxAutocomplete
            label="Utility"
            data={availableUtilities.map((u) => ({ id: u.cuid, name: `${u.shortName} — ${u.name}` }))}
            value={utilityCuid}
            handleChange={(value) => setUtilityCuid(value)}
            wildCardDisabled
            isNotFilter
            description={availableUtilities.length === 0 ? 'All utilities are already linked to this property.' : undefined}
          />
          <ModifiedDescriptionField value={modifiedDescription} onChange={setModifiedDescription} />
          {error != null && <p className="text-sm text-red-600 dark:text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 mt-2">
            <Button type="button" color="slate" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" isSaving={isSaving} onClick={handleSubmit} disabled={availableUtilities.length === 0}>
              Link utility
            </Button>
          </div>
        </div>
      </div>
    </CustomContentModal>
  )
}

// --------------------------------------------------------------------------
// Create energy tax modal (catalog row only; not linked to this property)
// --------------------------------------------------------------------------

interface CreateEnergyTaxModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
}

/** UI accepts percent with up to 4 decimal places; API stores a fraction with up to 6 decimal places. */
const percentToFraction = (percentText: string): number | null => {
  if (percentText.trim() === '') return null
  const percent = Number(percentText)
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) return null
  const scaled = percent * 1e4
  if (Math.abs(scaled - Math.round(scaled)) > 1e-6) return null
  return Math.round(scaled) / 1e6
}

const CreateEnergyTaxModal: React.FC<CreateEnergyTaxModalProps> = ({ isOpen, onClose, onCreated }) => {
  const dispatch = useAppDispatch()
  const [taxName, setTaxName] = useState('')
  const [code, setCode] = useState('')
  const [ratePercent, setRatePercent] = useState('')
  const [region, setRegion] = useState('')
  const [chargeTypes, setChargeTypes] = useState<UtilityChargeType[]>([])
  const [effectiveDate, setEffectiveDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [modifiedDescription, setModifiedDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setTaxName('')
      setCode('')
      setRatePercent('')
      setRegion('')
      setChargeTypes([])
      setEffectiveDate('')
      setEndDate('')
      setModifiedDescription('')
      setError(null)
    }
  }, [isOpen])

  const toggleChargeType = (chargeType: UtilityChargeType): void => {
    setChargeTypes((prev) => (prev.includes(chargeType) ? prev.filter((c) => c !== chargeType) : [...prev, chargeType]))
  }

  const handleSubmit = async (): Promise<void> => {
    const taxRate = percentToFraction(ratePercent)
    if (taxName.trim() === '') return setError('Tax name is required.')
    if (code.trim() === '') return setError('Code is required.')
    if (taxRate == null) return setError('Rate must be a percent between 0 and 100 with up to 4 decimal places.')
    if (region.trim() === '') return setError('Region is required.')
    if (chargeTypes.length === 0) return setError('Select at least one charge type.')
    if (effectiveDate === '') return setError('Effective date is required.')
    if (endDate !== '' && endDate < effectiveDate) return setError('End date must not be before the effective date.')
    if (modifiedDescription.trim() === '') return setError('Change reason is required.')

    setIsSaving(true)
    setError(null)
    try {
      await createEnergyTaxApi({
        taxName: taxName.trim(),
        code: code.trim(),
        taxRate,
        region: region.trim(),
        applicableChargeType: UTILITY_CHARGE_TYPES.filter((c) => chargeTypes.includes(c)),
        effectiveDate,
        endDate: endDate === '' ? null : endDate,
        modifiedDescription: modifiedDescription.trim(),
      })
      await dispatch(pushToast({ type: 'success', message: 'Energy tax created' }))
      onCreated()
      onClose()
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Something went wrong. Please try again.'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <CustomContentModal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white dark:bg-slate-800 px-5 py-4 w-full max-w-lg mx-auto shadow-xl rounded-2xl flex flex-col">
        <h2 className="mb-1 text-xl font-medium dark:text-white">New energy tax</h2>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          Adds a tax to the catalog. It is not attached to any utility; use Manage taxes to attach it.
        </p>
        <div className="flex flex-col gap-4">
          <Input
            label="Tax name"
            required
            maxLength={200}
            value={taxName}
            placeholder="e.g. Stockton Utility Users Tax"
            onChange={(e) => setTaxName(e.target.value)}
          />
          <Input
            label="Code"
            required
            maxLength={100}
            value={code}
            placeholder="e.g. stocktonUtilityTax"
            description="Billing matches some codes exactly. Copy the code from the billing config."
            onChange={(e) => setCode(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Rate (%)"
              required
              type="number"
              step="0.0001"
              min={0}
              max={100}
              value={ratePercent}
              placeholder="e.g. 0.75"
              onChange={(e) => setRatePercent(e.target.value)}
            />
            <Input
              label="Region"
              required
              maxLength={200}
              value={region}
              placeholder="e.g Stockton or California"
              onChange={(e) => setRegion(e.target.value)}
            />
          </div>
          <fieldset>
            <legend className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Applicable charge types<span className="ml-1 text-sm text-red-500">*</span>
            </legend>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {UTILITY_CHARGE_TYPES.map((chargeType) => (
                <label key={chargeType} className="flex items-center gap-2 text-sm dark:text-white cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600"
                    checked={chargeTypes.includes(chargeType)}
                    onChange={() => toggleChargeType(chargeType)}
                  />
                  {chargeType}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Effective date" required type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
            <Input
              label="End date"
              type="date"
              min={effectiveDate === '' ? undefined : effectiveDate}
              description="Leave blank for no planned end"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <ModifiedDescriptionField value={modifiedDescription} onChange={setModifiedDescription} />
          {error != null && <p className="text-sm text-red-600 dark:text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 mt-2">
            <Button type="button" color="slate" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" isSaving={isSaving} onClick={handleSubmit}>
              Create energy tax
            </Button>
          </div>
        </div>
      </div>
    </CustomContentModal>
  )
}

// --------------------------------------------------------------------------
// Manage taxes modal (attach/detach with confirmation summary)
// --------------------------------------------------------------------------

interface ManageTaxesModalProps {
  isOpen: boolean
  propertyId: string
  propertyUtility: PropertyUtilityRow | null
  attachedTaxes: PropertyUtilityTaxRow[]
  energyTaxCatalog: EnergyTaxCatalogItem[]
  onClose: () => void
  onSynced: () => void
}

const ManageTaxesModal: React.FC<ManageTaxesModalProps> = ({
  isOpen,
  propertyId,
  propertyUtility,
  attachedTaxes,
  energyTaxCatalog,
  onClose,
  onSynced,
}) => {
  const [showAll, setShowAll] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCuids, setSelectedCuids] = useState<Set<string>>(new Set())
  const [modifiedDescription, setModifiedDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [summary, setSummary] = useState<SyncPropertyUtilityTaxesResult | null>(null)

  useEffect(() => {
    if (isOpen) {
      setShowAll(false)
      setSearchTerm('')
      setSelectedCuids(new Set(attachedTaxes.map((t) => t.energyTax.cuid)))
      setModifiedDescription('')
      setError(null)
      setSummary(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, propertyUtility?.cuid])

  const attachedCuids = new Set(attachedTaxes.map((t) => t.energyTax.cuid))
  const term = searchTerm.trim().toLowerCase()

  const matchesSearch = (tax: EnergyTaxCatalogItem): boolean => {
    if (term === '') return true
    return (
      tax.taxName.toLowerCase().includes(term) ||
      tax.code.toLowerCase().includes(term) ||
      (tax.region ?? '').toLowerCase().includes(term) ||
      formatChargeTypes(tax.applicableChargeType, '').toLowerCase().includes(term)
    )
  }

  // Searching intentionally searches the whole catalog, ignoring the effective-only default,
  // so an expired or not-yet-effective tax can still be found by name/code/region.
  const visibleTaxes = (
    term !== ''
      ? energyTaxCatalog
      : showAll
        ? energyTaxCatalog
        : energyTaxCatalog.filter((t) => isCurrentlyEffective(t) || attachedCuids.has(t.cuid))
  ).filter(matchesSearch)

  const toggleTax = (cuid: string): void => {
    setSelectedCuids((prev) => {
      const next = new Set(prev)
      if (next.has(cuid)) {
        next.delete(cuid)
      } else {
        next.add(cuid)
      }
      return next
    })
  }

  const handleSubmit = async (): Promise<void> => {
    if (propertyUtility == null) return
    if (modifiedDescription.trim() === '') {
      setError('Change reason is required.')
      return
    }
    setIsSaving(true)
    setError(null)
    try {
      const result = await syncPropertyUtilityTaxesApi(propertyId, propertyUtility.cuid, {
        energyTaxCuids: Array.from(selectedCuids),
        modifiedDescription: modifiedDescription.trim(),
      })
      setSummary(result)
      onSynced()
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Something went wrong. Please try again.'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <CustomContentModal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white dark:bg-slate-800 px-5 py-4 w-full max-w-xl mx-auto shadow-xl rounded-2xl flex flex-col">
        <h2 className="mb-1 text-xl font-medium dark:text-white">Manage taxes</h2>
        {propertyUtility != null && (
          <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
            {propertyUtility.utility.shortName} — {propertyUtility.utility.name}
          </p>
        )}

        {summary != null ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-700 dark:text-slate-300">Changes saved:</p>
            <div className="text-sm">
              <div className="font-medium text-emerald-700 dark:text-emerald-400">Attached ({summary.attached.length})</div>
              {summary.attached.length === 0 ? (
                <div className="text-slate-500 dark:text-slate-400">None</div>
              ) : (
                summary.attached.map((t) => <div key={t.cuid}>{t.taxName} ({t.code})</div>)
              )}
            </div>
            <div className="text-sm">
              <div className="font-medium text-red-700 dark:text-red-400">Detached ({summary.detached.length})</div>
              {summary.detached.length === 0 ? (
                <div className="text-slate-500 dark:text-slate-400">None</div>
              ) : (
                summary.detached.map((t) => <div key={t.cuid}>{t.taxName} ({t.code})</div>)
              )}
            </div>
            <div className="text-sm">
              <div className="font-medium text-slate-600 dark:text-slate-400">Already present ({summary.alreadyPresent.length})</div>
              {summary.alreadyPresent.length === 0 ? (
                <div className="text-slate-500 dark:text-slate-400">None</div>
              ) : (
                summary.alreadyPresent.map((t) => <div key={t.cuid}>{t.taxName} ({t.code})</div>)
              )}
            </div>
            <div className="flex justify-end mt-2">
              <Button type="button" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Input
              label="Search taxes"
              placeholder="Search by name, code, region, or charge type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {term !== ''
                  ? `Showing search results for "${searchTerm.trim()}"`
                  : showAll
                    ? 'Showing all taxes'
                    : 'Showing currently effective taxes'}
              </span>
              {term === '' && (
                <button
                  type="button"
                  className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                  onClick={() => setShowAll((v) => !v)}
                >
                  {showAll ? 'Show effective only' : 'Show all taxes'}
                </button>
              )}
            </div>

            <div className="max-h-64 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-200 dark:divide-slate-700">
              {visibleTaxes.map((tax) => {
                const chargeTypeLabel = formatChargeTypes(tax.applicableChargeType, '—')
                return (
                  <label
                    key={tax.cuid}
                    className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/50"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600"
                      checked={selectedCuids.has(tax.cuid)}
                      onChange={() => toggleTax(tax.cuid)}
                    />
                    <div className="min-w-0 flex-1 text-sm">
                      <div className="font-medium dark:text-white">
                        {tax.taxName} <span className="text-slate-400 font-normal">({tax.code})</span>
                      </div>
                      <div className="flex min-w-0 items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                        <span className="shrink-0">{(tax.taxRate * 100).toFixed(3)}%</span>
                        <span className="shrink-0">·</span>
                        <span className="shrink-0">{tax.region ?? 'All regions'}</span>
                        <span className="shrink-0">·</span>
                        <span className="min-w-0 truncate" title={chargeTypeLabel}>
                          {chargeTypeLabel}
                        </span>
                        {!isCurrentlyEffective(tax) && (
                          <span className="shrink-0">· Not currently effective</span>
                        )}
                      </div>
                    </div>
                  </label>
                )
              })}
              {visibleTaxes.length === 0 && (
                <div className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400">
                  {term !== '' ? 'No taxes match your search.' : 'No taxes to show.'}
                </div>
              )}
            </div>

            <ModifiedDescriptionField value={modifiedDescription} onChange={setModifiedDescription} />
            {error != null && <p className="text-sm text-red-600 dark:text-red-500">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <Button type="button" color="slate" onClick={onClose}>
                Cancel
              </Button>
              <Button type="button" isSaving={isSaving} onClick={handleSubmit}>
                Save changes
              </Button>
            </div>
          </div>
        )}
      </div>
    </CustomContentModal>
  )
}

// --------------------------------------------------------------------------
// Main panel
// --------------------------------------------------------------------------

const PropertyUtilitiesAndTaxesPanel: React.FC<PropertyUtilitiesAndTaxesPanelProps> = ({ propertyId, propertyName, canModify }) => {
  const dispatch = useAppDispatch()
  const [propertyUtilities, setPropertyUtilities] = useState<PropertyUtilityRow[] | null>(null)
  const [attachedTaxesByPU, setAttachedTaxesByPU] = useState<Record<string, PropertyUtilityTaxRow[]>>({})
  const [utilityOptions, setUtilityOptions] = useState<UtilityOption[]>([])
  const [energyTaxCatalog, setEnergyTaxCatalog] = useState<EnergyTaxCatalogItem[]>([])
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [showCreateTaxModal, setShowCreateTaxModal] = useState(false)
  const [manageTaxesTarget, setManageTaxesTarget] = useState<PropertyUtilityRow | null>(null)
  const [confirmModal, setConfirmModal] = useState<React.ReactElement | boolean>(false)

  const syncAttachedTaxesFromRows = (rows: PropertyUtilityRow[]): void => {
    const taxesByPU: Record<string, PropertyUtilityTaxRow[]> = {}
    for (const row of rows) {
      taxesByPU[row.cuid] = row.attachedTaxes ?? []
    }
    setAttachedTaxesByPU(taxesByPU)
  }

  const fetchUtilities = async (): Promise<void> => {
    try {
      const rows = await listPropertyUtilitiesApi(propertyId)
      setPropertyUtilities(rows)
      syncAttachedTaxesFromRows(rows)
    } catch (e: unknown) {
      setPropertyUtilities([])
      setAttachedTaxesByPU({})
      void dispatch(
        pushToast({
          type: 'error',
          message: getApiErrorMessage(e, 'Could not load property utilities.'),
        })
      )
    }
  }

  const fetchUtilityOptions = async (): Promise<void> => {
    if (!canModify) {
      setUtilityOptions([])
      return
    }
    try {
      const options = await getPropertyUtilityOptionsApi(propertyId)
      setUtilityOptions(options)
    } catch (e: unknown) {
      setUtilityOptions([])
      void dispatch(
        pushToast({
          type: 'error',
          message: getApiErrorMessage(e, 'Could not load utility options.'),
        })
      )
    }
  }

  const refreshUtilitiesAndOptions = async (): Promise<void> => {
    await Promise.all([fetchUtilities(), fetchUtilityOptions()])
  }

  const refreshTaxesAfterSync = async (propertyUtilityCuid: string): Promise<void> => {
    try {
      const taxes = await listPropertyUtilityTaxesApi(propertyId, propertyUtilityCuid)
      setAttachedTaxesByPU((prev) => ({ ...prev, [propertyUtilityCuid]: taxes }))
      setPropertyUtilities((prev) =>
        prev?.map((row) => (row.cuid === propertyUtilityCuid ? { ...row, attachedTaxes: taxes } : row)) ?? null
      )
    } catch (e: unknown) {
      void dispatch(
        pushToast({
          type: 'error',
          message: getApiErrorMessage(e, 'Could not refresh attached taxes after save.'),
        })
      )
    }
  }

  const fetchEnergyTaxCatalog = async (options?: { isRefetch?: boolean }): Promise<void> => {
    try {
      setEnergyTaxCatalog(await getEnergyTaxCatalogApi())
    } catch (e: unknown) {
      if (!options?.isRefetch) {
        setEnergyTaxCatalog([])
      }
      void dispatch(
        pushToast({
          type: 'error',
          message: getApiErrorMessage(e, 'Could not load energy tax catalog.'),
        })
      )
    }
  }

  useEffect(() => {
    setPropertyUtilities(null)
    void refreshUtilitiesAndOptions()
    void fetchEnergyTaxCatalog()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, canModify])

  const handleUnlink = async (row: PropertyUtilityRow): Promise<void> => {
    try {
      await new Promise<void>((resolve, reject) => {
        setConfirmModal(
          <Modal
            resolve={() => resolve()}
            reject={reject}
            title="Unlink utility?"
            message={`This will remove the link between this property and ${row.utility.shortName}.`}
          />
        )
      })
      setConfirmModal(false)
      try {
        await unlinkPropertyUtilityApi(propertyId, row.cuid)
        await dispatch(pushToast({ type: 'success', message: 'Utility unlinked' }))
        void refreshUtilitiesAndOptions()
      } catch (e: unknown) {
        await dispatch(
          pushToast({
            type: 'error',
            message: 'Error unlinking utility',
            description: getApiErrorMessage(e, 'Could not unlink utility.'),
          })
        )
      }
    } catch {
      setConfirmModal(false)
    }
  }

  return (
    <>
      {confirmModal}
      <Panel
        header={
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span>Utilities and Taxes</span>
              {propertyName != null && <span className="text-xs font-normal text-slate-500 dark:text-slate-400">{propertyName}</span>}
            </div>
            {canModify && (
              <div className="flex items-center gap-2">
                <Button size={1} onClick={() => setShowCreateTaxModal(true)}>
                  New energy tax
                </Button>
                <Button size={1} onClick={() => setShowLinkModal(true)}>
                  Link utility
                </Button>
              </div>
            )}
          </div>
        }
      >
        {propertyUtilities == null ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading utilities...</div>
        ) : propertyUtilities.length === 0 ? (
          <div className="flex flex-col items-start gap-2 py-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              No utilities are linked to this property yet.{canModify ? ' Link one to get started.' : ''}
            </p>
            {canModify && (
              <Button size={1} onClick={() => setShowLinkModal(true)}>
                Link utility
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {propertyUtilities.map((pu) => {
              const attachedTaxes = attachedTaxesByPU[pu.cuid] ?? []
              return (
                <Disclosure key={pu.cuid} defaultOpen={false}>
                  {({ open }) => (
                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg">
                      <DisclosureButton className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-900/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge status={pu.utility.shortName} displayLabel={pu.utility.shortName} />
                          <span className="text-sm font-medium dark:text-white">{pu.utility.name}</span>
                          {pu.utility.type != null && (
                            <span className="text-xs text-slate-500 dark:text-slate-400">{pu.utility.type}</span>
                          )}
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {attachedTaxes.length} tax{attachedTaxes.length === 1 ? '' : 'es'}
                          </span>
                        </div>
                        <ChevronDownIcon className={`w-5 h-5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                      </DisclosureButton>
                      <DisclosurePanel className="border-t border-slate-200 dark:border-slate-700 px-4 py-3">
                        {attachedTaxes.length === 0 ? (
                          <p className="text-sm text-slate-500 dark:text-slate-400">No taxes attached.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                  <th className="pr-4 py-1">Tax</th>
                                  <th className="pr-4 py-1">Code</th>
                                  <th className="pr-4 py-1">Rate</th>
                                  <th className="pr-4 py-1">Region</th>
                                  <th className="pr-4 py-1">Charge Type</th>
                                  <th className="pr-4 py-1">Effective</th>
                                  <th className="pr-4 py-1">End</th>
                                </tr>
                              </thead>
                              <tbody>
                                {attachedTaxes.map((t) => (
                                  <tr key={t.cuid}>
                                    <td className="pr-4 py-1">{t.energyTax.taxName}</td>
                                    <td className="pr-4 py-1 font-mono text-xs">{t.energyTax.code}</td>
                                    <td className="pr-4 py-1">{(t.energyTax.taxRate * 100).toFixed(3)}%</td>
                                    <td className="pr-4 py-1">{t.energyTax.region ?? '—'}</td>
                                    <td className="max-w-xs whitespace-normal align-top pr-4 py-1">
                                      {formatChargeTypes(t.energyTax.applicableChargeType, '—')}
                                    </td>
                                    <td className="pr-4 py-1">
                                      {t.energyTax.effectiveDate != null ? formatUTCDate(t.energyTax.effectiveDate) : '—'}
                                    </td>
                                    <td className="pr-4 py-1">
                                      {t.energyTax.endDate != null ? formatUTCDate(t.energyTax.endDate) : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {canModify && (
                          <div className="flex justify-end gap-2 mt-3">
                            <Button size={1} color="slate" onClick={() => setManageTaxesTarget(pu)}>
                              Manage taxes
                            </Button>
                            <Button size={1} color="red" onClick={() => handleUnlink(pu)}>
                              Unlink utility
                            </Button>
                          </div>
                        )}
                      </DisclosurePanel>
                    </div>
                  )}
                </Disclosure>
              )
            })}
          </div>
        )}
      </Panel>

      <LinkUtilityModal
        isOpen={showLinkModal}
        propertyId={propertyId}
        availableUtilities={utilityOptions}
        onClose={() => setShowLinkModal(false)}
        onLinked={() => void refreshUtilitiesAndOptions()}
      />

      <CreateEnergyTaxModal
        isOpen={showCreateTaxModal}
        onClose={() => setShowCreateTaxModal(false)}
        onCreated={() => void fetchEnergyTaxCatalog({ isRefetch: true })}
      />

      <ManageTaxesModal
        isOpen={manageTaxesTarget != null}
        propertyId={propertyId}
        propertyUtility={manageTaxesTarget}
        attachedTaxes={manageTaxesTarget != null ? attachedTaxesByPU[manageTaxesTarget.cuid] ?? [] : []}
        energyTaxCatalog={energyTaxCatalog}
        onClose={() => setManageTaxesTarget(null)}
        onSynced={() => {
          if (manageTaxesTarget != null) {
            void refreshTaxesAfterSync(manageTaxesTarget.cuid)
          }
        }}
      />
    </>
  )
}

export default PropertyUtilitiesAndTaxesPanel
